import { type TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { CardImg } from '@sorare/core/src/components/card/CardImg';

import type { EligiblePlayers_commonPlayer } from './__generated__/index.graphql';

const OverflowX = styled.div`
  overflow-x: auto;
  max-width: 100%;
  position: relative;
`;

const Cards = styled(Horizontal)`
  gap: var(--unit);
  padding: var(--unit) var(--double-unit);
  box-shadow: inset 0 0 100px 1px var(--c-nd-100);
  min-width: fit-content;
`;

const StyledCardImg = styled(CardImg)`
  width: 48px;
`;

const LeftShadow = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;

  width: var(--double-unit);
  background: linear-gradient(
    to right,
    rgba(var(--c-rgb-black), 1) 10%,
    rgba(var(--c-rgb-black), 0) 100%
  );
`;

const RightShadow = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: var(--double-unit);
  background: linear-gradient(
    to left,
    rgba(var(--c-rgb-black), 1) 10%,
    rgba(var(--c-rgb-black), 0) 100%
  );
`;

type Props = {
  commonPlayers: EligiblePlayers_commonPlayer[];
  color: string;
};

export const EligiblePlayers = ({ commonPlayers, color }: Props) => {
  return (
    <Bezel
      background={`conic-gradient(from 90deg at 3px 3px, var(--c-nd-150-opaque) 25%, var(--c-black) 0) 0 0/5px 5px`}
      bezelSize={12}
      radius={8}
      borderWidth={1}
      borderColor={`linear-gradient(to bottom, var(--c-nd-100) 0%, ${color} 100%)`}
      boxShadow="0 0 0 20px var(--c-nd-100)"
    >
      <NeonEdge color="#D8CB8F" showNeon="always" showHalo="never">
        <OverflowX>
          <Cards>
            {commonPlayers?.map(
              ({ tinifiedPictureUrl, id, anyPlayer }) =>
                tinifiedPictureUrl && (
                  <StyledCardImg
                    key={id}
                    src={proxyUrl(tinifiedPictureUrl, {
                      cropWidth: 80,
                    })}
                    alt={`${anyPlayer.displayName}`}
                  />
                )
            )}
          </Cards>
        </OverflowX>
      </NeonEdge>
      <LeftShadow />
      <RightShadow />
    </Bezel>
  );
};

EligiblePlayers.fragments = {
  commonPlayer: gql`
    fragment EligiblePlayers_commonPlayer on CommonPlayer {
      id
      anyPlayer {
        slug
        displayName
      }
      tinifiedPictureUrl: pictureUrl(derivative: "tinified")
    }
  ` as TypedDocumentNode<EligiblePlayers_commonPlayer>,
};
